import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiClient } from "@/lib/api-client.js";
import { StatusBadge } from "@/components/StatusBadge.js";
import { Card } from "@/components/ui/Card.js";
import { PageHeader } from "@/components/ui/PageHeader.js";
import { Button } from "@/components/ui/Button.js";
import { EmptyState } from "@/components/ui/EmptyState.js";

interface ProjectItem {
  id: string;
  name: string;
  status: string;
  address: string;
  buildingType: string;
  customer: { id: string; name: string };
  risk?: "red" | "yellow" | "green" | "grey";
  confidencePct?: number | null;
  updatedAt: string;
  createdAt: string;
}

// §5 Risikoampel + §22 „nicht nur Farbe": Farbe + Symbol + Text.
const RISK_UI: Record<string, { color: string; symbol: string; label: string }> = {
  red: { color: "#dc2626", symbol: "●", label: "Kritisch" },
  yellow: { color: "#d97706", symbol: "▲", label: "Prüfen" },
  green: { color: "#16a34a", symbol: "✓", label: "OK" },
  grey: { color: "#94a3b8", symbol: "○", label: "Keine Daten" },
};
function RiskDot({ risk }: { risk: string }) {
  const r = RISK_UI[risk] ?? { color: "#94a3b8", symbol: "○", label: "Keine Daten" };
  return (
    <span title={r.label} style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, color: r.color, fontWeight: 600 }}>
      <span aria-hidden="true">{r.symbol}</span>{r.label}
    </span>
  );
}

interface ProjectListData {
  items: ProjectItem[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export function ProjectListPage() {
  const [items, setItems] = useState<ProjectItem[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    void load();
  }, [debouncedSearch, statusFilter, page]); // load depends on filter state

  async function load() {
    setLoading(true);
    const params = new URLSearchParams();
    if (debouncedSearch) params.set("search", debouncedSearch);
    if (statusFilter) params.set("status", statusFilter);
    params.set("page", String(page));
    params.set("pageSize", "20");

    const res = await apiClient.get<ProjectListData>(`/projects?${params.toString()}`);
    if (res.success) {
      setItems(res.data.items);
      setTotal(res.data.total);
      setTotalPages(res.data.totalPages ?? 1);
    }
    setLoading(false);
  }

  const hasFilter = debouncedSearch !== "" || statusFilter !== "";

  return (
    <div>
      <PageHeader
        title="Projekte"
        subtitle={loading ? undefined : `${total} Projekt${total !== 1 ? "e" : ""} gesamt`}
        actions={<Link to="/projects/new"><Button icon="plus">Neues Projekt</Button></Link>}
      />

      {/* Search + status filter */}
      <div className="cluster" style={{ marginBottom: "var(--space-4)", flexWrap: "nowrap" }}>
        <input
          placeholder="Projekt suchen…"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          style={{ flex: 1 }}
        />
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          style={{ width: 200, flexShrink: 0 }}
        >
          <option value="">Alle Status</option>
          <option value="draft">Entwurf</option>
          <option value="in_planning">In Planung</option>
          <option value="in_review">In Prüfung</option>
          <option value="ready_for_offer">Angebotsbereit</option>
          <option value="archived">Archiviert</option>
        </select>
      </div>

      <Card flush>
        {loading ? (
          <div style={{ textAlign: "center", padding: 48, color: "var(--color-text-muted)" }}>Lade Projekte…</div>
        ) : !hasFilter && items.length === 0 ? (
          <EmptyState
            icon="projects"
            title="Noch keine Projekte vorhanden"
            description="Lege dein erstes Projekt an, um mit der Planung zu beginnen."
            action={<Link to="/projects/new"><Button icon="plus">Erstes Projekt anlegen</Button></Link>}
          />
        ) : items.length === 0 && hasFilter ? (
          <EmptyState
            icon="search"
            title="Keine Projekte gefunden"
            description={`Für „${debouncedSearch || statusFilter}" wurden keine Projekte gefunden.`}
            action={
              <Button variant="secondary" onClick={() => { setSearch(""); setStatusFilter(""); setPage(1); }}>
                Filter zurücksetzen
              </Button>
            }
          />
        ) : (
          <>
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    {["Projekt", "Kunde", "Gebäudetyp", "Adresse", "Status", "Risiko", "Vertrauen", "Geändert"].map((h) => (
                      <th key={h}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.map((p) => (
                    <tr key={p.id}>
                      <td><Link to={`/projects/${p.id}`}>{p.name}</Link></td>
                      <td>{p.customer.name}</td>
                      <td className="muted">{p.buildingType}</td>
                      <td className="muted">{p.address}</td>
                      <td><StatusBadge status={p.status} /></td>
                      <td><RiskDot risk={p.risk ?? "grey"} /></td>
                      <td className="muted">{p.confidencePct != null ? `${p.confidencePct} %` : "—"}</td>
                      <td className="muted">{new Date(p.updatedAt).toLocaleDateString("de-AT")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 12, padding: "var(--space-4)" }}>
                <Button variant="secondary" size="sm" icon="chevronLeft" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
                  Zurück
                </Button>
                <span style={{ fontSize: 13, color: "var(--color-text-muted)" }}>
                  Seite {page} von {totalPages}
                </span>
                <Button variant="secondary" size="sm" iconRight="chevronRight" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                  Weiter
                </Button>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
